import React, { useState, useEffect } from 'react';
import { Loader2, Sparkles, Search, Trash2 } from 'lucide-react';
import { getInfo, fuzzySearch, fuzzyDeduplicate, fuzzyDeduplicatePreview } from '../../utils/api';
import { toast } from 'sonner';
import { useT } from '../../i18n';
import MissingChoice from './MissingChoice';

const FuzzySearchOperation = ({ onSuccess, onCancel }) => {
  const t = useT();
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [applying, setApplying] = useState(false);
  // What the current threshold would remove. Null until asked for; cleared the
  // moment any setting changes, so what is on screen always describes the
  // settings that are on screen.
  const [plan, setPlan] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [mode, setMode] = useState('deduplicate'); // 'search' or 'deduplicate'
  
  const [config, setConfig] = useState({
    column: '',
    search_value: '',
    threshold: 0.85,
    limit: 50,
    keep: 'first'
  });

  useEffect(() => {
    loadColumns();
  }, []);

  const loadColumns = async () => {
    try {
      const result = await getInfo();
      setColumns(result.info.column_names || []);
    } catch (error) {
      toast.error(t('form.loadColumnsFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!config.column || !config.search_value) {
      toast.error(t('fuzzy.needColumnAndValue'));
      return;
    }

    try {
      setSearching(true);
      const result = await fuzzySearch({
        column: config.column,
        search_value: config.search_value,
        threshold: config.threshold,
        limit: config.limit
      });
      setSearchResults(result.results || []);
      if (result.results?.length === 0) {
        toast.info(t('fuzzy.noMatch'));
      } else {
        toast.success(t('fuzzy.found', { count: result.count }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('fuzzy.searchFailed'));
    } finally {
      setSearching(false);
    }
  };

  // Deduplicating is the most destructive operation here and the least
  // predictable: the threshold's effect depends entirely on the data. On a
  // column of work addresses everyone shares a domain, so at 70% — which reads
  // as "quite similar" — most of the column folds into a handful of rows, none
  // of them a real duplicate, and the operation reports success. So nothing is
  // removed until what would go has been shown.
  const handlePreview = async () => {
    if (!config.column) {
      toast.error(t('fuzzy.needColumn'));
      return;
    }
    try {
      setApplying(true);
      setPlan(await fuzzyDeduplicatePreview({
        column: config.column,
        threshold: config.threshold,
        keep: config.keep
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('fuzzy.failed'));
    } finally {
      setApplying(false);
    }
  };

  const handleDeduplicate = async () => {
    try {
      setApplying(true);
      const result = await fuzzyDeduplicate({
        column: config.column,
        threshold: config.threshold,
        keep: config.keep
      });
      onSuccess(result);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('fuzzy.failed'));
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 text-muted-foreground">
        <Sparkles className="h-5 w-5" />
        <p className="text-sm">{t('fuzzy.intro')}</p>
      </div>

      {/* Mode selector */}
      <div className="flex gap-2 p-1 bg-muted rounded-lg">
        <button
          type="button"
          onClick={() => setMode('deduplicate')}
          className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-colors ${
            mode === 'deduplicate'
              ? 'bg-card shadow-sm text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="fuzzy-mode-deduplicate"
        >
          <Trash2 className="h-4 w-4" />
          {t('fuzzy.modeDeduplicate')}
        </button>
        <button
          type="button"
          onClick={() => setMode('search')}
          className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-sm font-medium transition-colors ${
            mode === 'search'
              ? 'bg-card shadow-sm text-foreground'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          data-testid="fuzzy-mode-search"
        >
          <Search className="h-4 w-4" />
          {t('fuzzy.modeSearch')}
        </button>
      </div>

      {/* Column selection */}
      <div>
        <label className="label mb-2 block">{t('fuzzy.selectColumn')}</label>
        <select
          value={config.column}
          onChange={(e) => { setPlan(null); setConfig(prev => ({ ...prev, column: e.target.value })); }}
          className="select"
          data-testid="fuzzy-column-select"
        >
          <option value="">{t('email.selectColumn')}</option>
          {columns.map(col => (
            <option key={col} value={col}>{col}</option>
          ))}
        </select>
      </div>

      {/* Search value (only for search mode) */}
      {mode === 'search' && (
        <div>
          <label className="label mb-2 block">{t('fuzzy.searchValue')}</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={config.search_value}
              onChange={(e) => setConfig(prev => ({ ...prev, search_value: e.target.value }))}
              placeholder={t('fuzzy.searchPlaceholder')}
              className="input flex-1"
              data-testid="fuzzy-search-value"
            />
            <button
              onClick={handleSearch}
              disabled={searching || !config.column || !config.search_value}
              className="btn-primary flex items-center gap-2"
              data-testid="fuzzy-search-btn"
            >
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              {t('fuzzy.searchAction')}
            </button>
          </div>
        </div>
      )}

      {/* Similarity threshold */}
      <div>
        <label className="label mb-2 block">
          {t('fuzzy.threshold')} <span className="text-primary font-semibold">{Math.round(config.threshold * 100)}%</span>
        </label>
        <input
          type="range"
          min="0.5"
          max="0.99"
          step="0.01"
          value={config.threshold}
          onChange={(e) => { setPlan(null); setConfig(prev => ({ ...prev, threshold: parseFloat(e.target.value) })); }}
          className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
          data-testid="fuzzy-threshold"
        />
        <div className="flex justify-between text-xs text-muted-foreground mt-1">
          <span>{t('fuzzy.thresholdLoose')}</span>
          <span>{t('fuzzy.thresholdStrict')}</span>
        </div>
      </div>

      {/* Keep option (only for deduplicate mode) */}
      {mode === 'deduplicate' && (
        <div>
          <label className="label mb-2 block">{t('fuzzy.keepWhich')}</label>
          <div className="flex gap-2">
            {['first', 'last'].map(option => (
              <button
                key={option}
                type="button"
                onClick={() => { setPlan(null); setConfig(prev => ({ ...prev, keep: option })); }}
                className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                  config.keep === option
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted hover:bg-muted/80'
                }`}
                data-testid={`fuzzy-keep-${option}`}
              >
                {option === 'first' ? t('fuzzy.keepFirst') : t('fuzzy.keepLast')}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Search results */}
      {mode === 'search' && searchResults.length > 0 && (
        <div>
          <label className="label mb-2 block">{t('fuzzy.results', { count: searchResults.length })}</label>
          <div className="max-h-60 overflow-y-auto border border-border rounded-lg">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('fuzzy.resultValue')}</th>
                  <th>{t('fuzzy.resultSimilarity')}</th>
                </tr>
              </thead>
              <tbody>
                {searchResults.map((result, idx) => (
                  <tr key={idx}>
                    <td className="font-mono text-sm">{result.value}</td>
                    <td>
                      <span className={`badge ${
                        result.similarity >= 90 ? 'badge-success' :
                        result.similarity >= 75 ? 'badge-warning' : 'badge-primary'
                      }`}>
                        {result.similarity}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* What this threshold would remove — shown before anything is removed */}
      {mode === 'deduplicate' && plan && (
        <div className="card p-4 space-y-3" data-testid="fuzzy-plan">
          <h4 className="text-sm font-semibold">
            {plan.rows_removed === 0
              ? t('fuzzy.planNothing')
              : t('fuzzy.planSummary', {
                  rows: plan.rows_removed,
                  total: plan.rows_total,
                  groups: plan.groups_total,
                })}
          </h4>
          {plan.rows_removed > 0 && (
            <div className="max-h-56 overflow-y-auto space-y-3">
              {plan.groups.map((group, index) => (
                <div key={index} className="text-xs border-l-2 border-primary/40 pl-3">
                  <div className="font-medium">{t('fuzzy.planKept', { value: String(group.kept) })}</div>
                  <ul className="text-muted-foreground mt-1 space-y-0.5">
                    {group.folded.map((folded, position) => (
                      <li key={position}>
                        {t('fuzzy.planFolded', {
                          value: String(folded.value),
                          similarity: Math.round(folded.similarity * 100),
                        })}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              {plan.truncated && (
                <p className="text-xs text-muted-foreground">
                  {t('fuzzy.planTruncated', { shown: plan.groups.length, total: plan.groups_total })}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Info box */}
      <div className="p-4 bg-muted/50 rounded-lg">
        <h4 className="text-sm font-medium mb-2">{t('fuzzy.howItWorks')}</h4>
        <p className="text-xs text-muted-foreground">
          {mode === 'deduplicate' 
            ? t('fuzzy.howDeduplicate')
            : t('fuzzy.howSearch')
          }
        </p>
      </div>

      {/* Actions */}
      <MissingChoice reasons={[!config.column && t('fuzzy.needColumn')]} />

      <div className="flex justify-end gap-3 pt-4 border-t border-border">
        <button onClick={onCancel} className="btn-secondary" data-testid="fuzzy-cancel">
          {t('form.cancel')}
        </button>
        {mode === 'deduplicate' && !plan && (
          <button
            onClick={handlePreview}
            disabled={applying || !config.column}
            className="btn-primary flex items-center gap-2"
            data-testid="fuzzy-preview"
          >
            {applying && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('fuzzy.preview')}
          </button>
        )}
        {mode === 'deduplicate' && plan && (
          <button
            onClick={handleDeduplicate}
            disabled={applying || plan.rows_removed === 0}
            className="btn-primary flex items-center gap-2"
            data-testid="fuzzy-apply"
          >
            {applying && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('fuzzy.confirm', { rows: plan.rows_removed })}
          </button>
        )}
      </div>
    </div>
  );
};

export default FuzzySearchOperation;
